import {
  Badge,
  BadgeVariantEnum,
  ChevronDownIcon,
  Chip,
  ChipColorEnum,
  ChipShapeEnum,
  ChipSizeEnum,
  Column,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  matchString,
  Table,
  TableOptions,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { groupItemsByKeywords } from '@/features/broadcast-schedule-program/helpers/group-by-id';
import { TSegmentMention } from '@/features/segments/types';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

import { BroadcastScheduleProgramBodySegmentToolkitMentionSubTable } from './BroadcastScheduleProgramBodySegmentToolkitMentionSubTable';

export type TGroupedMention = {
  id: string;
  children: TSegmentMention[];
};

type TBroadcastScheduleTableProps = {
  items: TSegmentMention[];
  onPreview?: (item: TSegmentMention) => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitMentionTable({
  items,
  onPreview,
}: TBroadcastScheduleTableProps) {
  const { activeSegment } = useBroadcastScheduleProgram();
  const { t } = useTranslation();

  const groupedKeywords = useMemo(() => groupItemsByKeywords(items), [items]);

  const groupedKeywordsUniqueDatetime = useMemo(() => {
    return groupedKeywords.map(item => {
      const seen = new Set<string>();
      const uniqueChildren: typeof item.children = [];

      item.children.forEach(child => {
        if (!seen.has(child.start_datetime)) {
          seen.add(child.start_datetime);
          uniqueChildren.push(child);
        }
      });

      return {
        ...item,
        children: uniqueChildren,
      } as TGroupedMention;
    });
  }, [groupedKeywords]);

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    filters: [],
    searchBarPlaceholder: t('Buscar por mención'),
  };

  const columns: Column<TGroupedMention>[] = useMemo(
    () => [
      {
        id: 'mention',
        searchable: true,
        matchFn: (query, item) => {
          return matchString((item?.id ?? '').toString(), query);
        },
        title: t('Menciones'),
        render: item => {
          return (
            <Chip color={ChipColorEnum.GHOST} shape={ChipShapeEnum.ROUNDED} size={ChipSizeEnum.SM}>
              {item?.id}
            </Chip>
          );
        },
      },
      {
        id: 'repetitions',
        title: t('Repeticiones'),
        render: item => {
          return item?.children && item.children?.length > 1 ? (
            <Badge variant={BadgeVariantEnum.DefaultFilled}>{item.children.length}</Badge>
          ) : (
            <></>
          );
        },
      },
      {
        id: 'timeline',
        title: t('Timeline'),
        render: item => {
          if (!activeSegment) return <></>;

          const seconds = getOffsetHHMMSS(
            item.children[0].start_datetime,
            activeSegment.start_datetime
          );
          return item?.children && item.children?.length > 1 ? (
            <></>
          ) : (
            <TableRow content={seconds} />
          );
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: (item, _index, _rows, showSubArrows, subArrowsAreVisible) => (
          <div className="inline-flex w-full justify-end">
            {item?.children && item.children?.length > 1 ? (
              <>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton className="pointer-events-none invisible">
                  <ChevronDownIcon size={IconSizeEnum.XS} />
                </IconButton>
                <IconButton
                  color={IconButtonColorEnum.NONE_DEFAULT}
                  shape={IconButtonShapeEnumOld.OLD_SQUARED}
                  onClick={() => showSubArrows(prevVal => !prevVal)}
                >
                  <ChevronDownIcon
                    size={IconSizeEnum.XS}
                    className={clsx(
                      'duration-300 ease-in-out',
                      subArrowsAreVisible && '-rotate-180'
                    )}
                  />
                </IconButton>
              </>
            ) : (
              <RenderActionButtons item={item} onPreview={() => onPreview?.(item.children[0])} />
            )}
          </div>
        ),
      },
    ],
    [t, groupedKeywordsUniqueDatetime, activeSegment]
  );

  return (
    <Table<any>
      options={options}
      columns={columns}
      data={groupedKeywordsUniqueDatetime}
      renderAdditionalRow={(item: TGroupedMention) => (
        <BroadcastScheduleProgramBodySegmentToolkitMentionSubTable
          item={item}
          columns={columns}
          onPreview={onPreview}
        />
      )}
      ignoreWrap
    />
  );
}
